import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { fetchApi } from "@/services/apiService";

export interface PipelineActivityEntry {
  id: string;
  leadId: number;
  type: "Stage Change";
  icon: string;
  description: string;
  agent: string;
  timestamp: string;
  systemEvent: true;
}

interface PipelineActivityContextType {
  activities: PipelineActivityEntry[];
  logStageChange: (entry: Omit<PipelineActivityEntry, "id" | "type" | "icon" | "systemEvent">) => void;
  logDealAdded: (entry: { leadId: number; stage: string; agent: string; leadName: string }) => void;
  getActivitiesForLead: (leadId: number) => PipelineActivityEntry[];
  allActivities: PipelineActivityEntry[];
}

const PipelineActivityContext = createContext<PipelineActivityContextType>({
  activities: [],
  logStageChange: () => {},
  logDealAdded: () => {},
  getActivitiesForLead: () => [],
  allActivities: [],
});

export function usePipelineActivities() {
  return useContext(PipelineActivityContext);
}

export function PipelineActivityProvider({ children }: { children: ReactNode }) {
  const [activities, setActivities] = useState<PipelineActivityEntry[]>([]);

  const logStageChange = useCallback(
    (entry: Omit<PipelineActivityEntry, "id" | "type" | "icon" | "systemEvent">) => {
      const newEntry: PipelineActivityEntry = {
        ...entry,
        id: `pipeline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: "Stage Change",
        icon: "ri-arrow-left-right-line",
        systemEvent: true,
      };
      setActivities((prev) => [newEntry, ...prev]);

      // Persist to central activities
      fetchApi("/activities", {
        method: "POST",
        body: JSON.stringify({
          type: "stage_change",
          title: "Stage changed",
          description: entry.description,
          user_name: entry.agent || "System",
          lead_id: String(entry.leadId),
          detail: entry.description,
        }),
      }).catch(() => {});

      if (entry.leadId) {
        fetchApi(`/leads/${entry.leadId}/activities`, {
          method: "POST",
          body: JSON.stringify({
            type: "Stage Change",
            description: entry.description,
            performedBy: entry.agent || "System",
          }),
        }).catch(() => {});
      }
    },
    []
  );

  const logDealAdded = useCallback(
    (entry: { leadId: number; stage: string; agent: string; leadName: string }) => {
      const newEntry: PipelineActivityEntry = {
        id: `pipeline-add-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        leadId: entry.leadId,
        type: "Stage Change",
        icon: "ri-arrow-left-right-line",
        description: `Added to pipeline — ${entry.stage} by ${entry.agent}`,
        agent: entry.agent,
        timestamp: "Just now",
        systemEvent: true,
      };
      setActivities((prev) => [newEntry, ...prev]);

      fetchApi("/activities", {
        method: "POST",
        body: JSON.stringify({
          type: "stage_change",
          title: "Added to pipeline",
          description: `Added to pipeline — ${entry.stage}`,
          user_name: entry.agent || "System",
          lead_name: entry.leadName,
          lead_id: String(entry.leadId),
          detail: `Added to pipeline — ${entry.stage}`,
        }),
      }).catch(() => {});
    },
    []
  );

  const getActivitiesForLead = useCallback(
    (leadId: number): PipelineActivityEntry[] => {
      return activities.filter((a) => a.leadId === leadId);
    },
    [activities]
  );

  return (
    <PipelineActivityContext.Provider
      value={{ activities, logStageChange, logDealAdded, getActivitiesForLead, allActivities: activities }}
    >
      {children}
    </PipelineActivityContext.Provider>
  );
}